import { Box, Button, Typography } from '@mui/material';
import { FieldProps } from 'formik';
import { useCallback } from 'react';

import { FormPart } from '@modules/shared/components/FormPart';
import { mapGridIndexes } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { CommonButtonWrapper, Wrapper } from '@styles/CommonStyles';
import { colorTheme } from '@styles/muiTheme';

import { parser } from './FormulaModal';
import { columnData, IFormulaFormProps } from './interfaces';

interface IRenderField extends FieldProps {
  label: string;
}

const RenderField = ({ field, label }: IRenderField) => {
  return (
    <Box display="flex" alignItems="flex-start" gap={1} pl={1}>
      <Typography width="6%" fontSize="11px" fontWeight={600}>{label}:</Typography>
      <Typography width="94%" fontWeight={600} color={colorTheme.palette.primary.main}>{field?.value ?? ''}</Typography>
    </Box>
  );
};

const formPart = [
  {
    name: 'state',
    label: 'State',
    width: '100%',
    component: RenderField,
  },
  {
    name: 'counties',
    label: 'County',
    width: '100%',
    component: RenderField,
  },
  {
    name: 'condition.minimumLoanAmount',
    label: 'Minimum Loan Amount',
    width: '30%',
    component: NumberInputField
  },
  {
    name: 'condition.maximumLoanAmount',
    label: 'Maximum Loan Amount',
    width: '35%',
    component: NumberInputField
  },
  {
    name: 'condition.maximumFeeAmount',
    label: 'Maximum Fee Amount',
    width: '35%',
    component: NumberInputField
  },
  {
    name: 'formula',
    label: 'Formula',
    width: '100%',
  },
  {
    name: 'calculatedAmount',
    label: 'Calculated Amount',
    width: '30%',
    component: NumberInputField,
    disabled: true,
    allowNegative: true,
  }
];

const columns = [
  dataGridCol('name', {
    width: 100
  }),
  dataGridCol('description', {
    flex: 1,
  }),
  dataGridCol('syntax', {
    flex: 1,
  })
].map((column) => ({ ...column }));

export const FormulaForm = ({ handleClose, varValues, values, setFieldValue, loading }: IFormulaFormProps) => {
  const handleClick = useCallback(() => {
    try {
      const exp = values?.formula && values.formula;
      const expr = parser.parse(exp);

      const value = expr.evaluate(varValues);
      setFieldValue('calculatedAmount', value);
    } catch {
      Notistack.generalError('Invalid Formula');
    }
  }, [values?.formula, varValues]);

  return (
    <>
      <FormPart config={formPart} />
      <Button variant="contained" disabled={!values?.formula} onClick={handleClick}>Calculate</Button>

      <Wrapper>
        <PhoenixGrid
          noButton
          withHeader
          autoHeight
          hideFooter
          disableSelectionOnClick
          name="Formula Function"
          pageSize={15}
          columns={columns}
          rows={mapGridIndexes(columnData)}
        />
      </Wrapper>
      <CommonButtonWrapper sx={{ position: 'relative', top: '80px' }}>
        <CancelButton
          label="Cancel"
          onClick={handleClose}
        />
        <SaveButton isLoading={loading} />
      </CommonButtonWrapper>
    </>
  );
};